import type { ChartRange, PostChartPoint, TwitterAccountSummary } from '@wml-crm/shared';
import { colorForAccount } from '../charts/palette.js';
import { formatSubName } from '../lib/subName.js';
import { StarBadge } from './StarButton.js';

interface Props {
  points: PostChartPoint[];
  accounts: TwitterAccountSummary[];
  modelDisplayName: string;
  range: ChartRange;
  onRangeChange: (r: ChartRange) => void;
  limit?: number;
  onSelect: (p: PostChartPoint) => void;
}

const RANGES: { key: ChartRange; label: string }[] = [
  { key: '1m', label: '1 мес' },
  { key: '3m', label: '3 мес' },
  { key: '6m', label: '6 мес' },
  { key: 'all', label: 'Всё' },
];

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y?.slice(2)}`;
}

export function TopPostsList({ points, accounts, modelDisplayName, range, onRangeChange, limit = 30, onSelect }: Props) {
  const top = [...points].sort((a, b) => b.views - a.views).slice(0, limit);
  const accById = new Map(accounts.map((a) => [a.id, a]));

  return (
    <div className="card card-large top-posts">
      <div className="account-toggler-head" style={{ marginBottom: 14 }}>
        <div>
          <div className="section-title" style={{ margin: 0, fontSize: 14 }}>
            Топ-{limit} постов
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 2 }}>
            по охватам · {top.length} из {points.length} · клик — открыть пост
          </div>
        </div>
        <div className="range-tabs range-tabs-compact">
          {RANGES.map((r) => (
            <button
              key={r.key}
              className={range === r.key ? 'active' : ''}
              onClick={() => onRangeChange(r.key)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {top.length === 0 ? (
        <div className="empty-state">Постов в периоде нет.</div>
      ) : (
        <ol className="top-posts-list">
          {top.map((p, i) => {
            const acc = accById.get(p.accountId);
            const color = colorForAccount(p.accountId);
            return (
              <li key={p.postId}>
                <button
                  className="top-post-row"
                  style={{ gridTemplateColumns: '32px 10px minmax(0, 1fr) 92px 82px 110px 42px 18px' }}
                  onClick={() => onSelect(p)}
                >
                  <span className="top-post-rank">{i + 1}</span>
                  <span className="acc-dot" style={{ background: color, borderColor: color }} />
                  <span className="top-post-acc">
                    {formatSubName(acc?.slaveName ?? p.slaveName, modelDisplayName) || (acc?.slaveName ?? p.slaveName)}
                  </span>
                  <span className="top-post-date">{formatDate(p.postDate)}</span>
                  <span className="top-post-days" title="Дней в работе">
                    {p.daysInWork} {p.daysInWork === 1 ? 'день' : (p.daysInWork < 5 ? 'дня' : 'дней')}
                  </span>
                  <span className="top-post-views">{p.views.toLocaleString('ru-RU')}</span>
                  <StarBadge count={p.reuseCount ?? 0} />
                  <span className="top-post-open" aria-hidden>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
